/**
 * WorkshopTypeCombobox — typeahead picker for the Basics workshop-type field
 * (BON-117 operator rework: a combobox, not a plain <select>). The user can
 * TYPE to filter the options and click (or arrow + Enter) to select. The input
 * shows the selected option's label; focusing/typing opens the filtered list.
 *
 * Controlled by `value` (the selected type id) + `onChange`. Options are the
 * active workshop types (plus the current one) resolved by the parent.
 */
import { useMemo, useRef, useState } from 'react';

import { fieldStyle } from '../../venues/venueCreateFields';

export interface ComboboxOption {
  id: string;
  label: string;
}

interface Props {
  value: string;
  options: ComboboxOption[];
  onChange: (id: string) => void;
  error?: boolean;
}

const LIST_STYLE: React.CSSProperties = {
  position: 'absolute',
  zIndex: 20,
  top: 'calc(100% + 2px)',
  left: 0,
  right: 0,
  margin: 0,
  padding: 4,
  listStyle: 'none',
  maxHeight: 220,
  overflowY: 'auto',
  background: 'var(--nb-bg)',
  border: '1px solid var(--nb-border-2)',
  borderRadius: 8,
  boxShadow: '0 8px 24px rgba(0,0,0,.18)',
};

/** The open dropdown of filtered options. `onPick` fires on mouseDown (before
 *  the input's blur) so selection lands without the list closing first. */
function ComboboxList({
  options,
  value,
  onPick,
}: {
  options: ComboboxOption[];
  value: string;
  onPick: (id: string) => void;
}) {
  return (
    <ul role="listbox" style={LIST_STYLE}>
      {options.map((o) => (
        <li key={o.id} style={{ listStyle: 'none' }}>
          {/* role="option" + the select handler live on the SAME element so a
              click on the option fires selection (events don't propagate to a
              child handler). onMouseDown beats the input's blur-close. */}
          <button
            type="button"
            role="option"
            aria-selected={o.id === value}
            // Explicit name: the combobox sits inside the Field's <label>, which
            // would otherwise lend every option its own "Workshop type" name.
            aria-label={o.label}
            onMouseDown={(e) => {
              e.preventDefault();
              onPick(o.id);
            }}
            style={{
              display: 'block',
              width: '100%',
              textAlign: 'left',
              padding: '6px 8px',
              fontSize: 12.5,
              borderRadius: 6,
              border: 'none',
              cursor: 'pointer',
              color: 'var(--nb-ink)',
              background: o.id === value ? 'var(--nb-primary-soft)' : 'transparent',
            }}
          >
            {o.label}
          </button>
        </li>
      ))}
    </ul>
  );
}

export function WorkshopTypeCombobox({ value, options, onChange, error }: Props) {
  const selected = options.find((o) => o.id === value) ?? null;
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const blurTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  // While open the input reflects what the user is typing; while closed it
  // shows the selected label.
  const display = open ? query : (selected?.label ?? '');
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!open || q === '') return options;
    return options.filter((o) => o.label.toLowerCase().includes(q));
  }, [open, query, options]);

  const openList = () => {
    setQuery('');
    setOpen(true);
  };
  const choose = (id: string) => {
    onChange(id);
    setOpen(false);
  };

  return (
    <div style={{ position: 'relative' }}>
      <input
        type="text"
        role="combobox"
        aria-expanded={open}
        aria-label="Workshop type"
        value={display}
        placeholder="Search workshop types…"
        onFocus={openList}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
        }}
        onBlur={() => {
          // Delay so an option's onMouseDown/onClick lands before we close.
          blurTimer.current = setTimeout(() => setOpen(false), 120);
        }}
        style={fieldStyle(error ? 'error' : undefined)}
      />
      {open && filtered.length > 0 && (
        <ComboboxList
          options={filtered}
          value={value}
          onPick={(id) => {
            if (blurTimer.current) clearTimeout(blurTimer.current);
            choose(id);
          }}
        />
      )}
    </div>
  );
}
